import { VALID_VARIANT_STRINGS } from '@draughts/shared';

/**
 * Device-local memory of a shared daily-puzzle link whose recipient still has
 * to authenticate.
 *
 * The daily puzzle is a registered-only feature, so a guest (or logged-out)
 * visitor arriving on `/?puzzle=daily&variant=…` is shown the invite screen
 * instead of the board. Logging in resumes them straight into the puzzle — but
 * REGISTERING does not: `register()` does not authenticate, it asks for an
 * email verification, and the verification link is typically opened from a mail
 * client in a NEW browsing context. Without this record the link would simply be
 * lost at exactly the moment the visitor did what we asked them to do.
 *
 * localStorage, not sessionStorage: sessionStorage is per-tab and would be empty
 * in precisely that case. Residual limitation, accepted: an in-app webview and
 * the system browser are separate storage partitions, so a link opened in one
 * and verified in the other is still lost — the visitor re-taps the shared link.
 *
 * Cleared when the puzzle finally opens, when the invite screen is left, and on
 * logout (shared devices must not resurrect someone else's link).
 */

const STORAGE_KEY = 'draughts-pending-puzzle-link';
/** Belt-and-braces against clock/timezone skew; `day` is the real guard. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface PendingPuzzleLink {
  /** Lowercased variant string, e.g. 'italian'. */
  variant: string;
  /** UTC day the link was stored (YYYY-MM-DD) — see the staleness rule below. */
  day: string;
  ts: number;
}

/** UTC day stamp, matching the server's daily-puzzle rotation. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * The pending link, or null. Self-clears anything unusable so a bad record
 * cannot linger: malformed JSON, an unknown variant, or a stale link.
 *
 * Staleness matters more than it looks: the daily puzzle rotates every UTC day,
 * so resuming yesterday's link would silently open a DIFFERENT puzzle from the
 * one the sender actually solved and shared.
 */
export function getPendingPuzzleLink(): PendingPuzzleLink | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<PendingPuzzleLink>;
    const { variant, day, ts } = parsed;
    if (
      typeof variant !== 'string' ||
      !VALID_VARIANT_STRINGS.includes(variant) ||
      typeof day !== 'string' ||
      day !== today() ||
      typeof ts !== 'number' ||
      Date.now() - ts > MAX_AGE_MS
    ) {
      clearPendingPuzzleLink();
      return null;
    }
    return { variant, day, ts };
  } catch {
    clearPendingPuzzleLink();
    return null;
  }
}

export function setPendingPuzzleLink(variant: string): void {
  try {
    const payload: PendingPuzzleLink = { variant, day: today(), ts: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Private mode / storage disabled — the link simply won't be resumed.
  }
}

export function clearPendingPuzzleLink(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do.
  }
}
